"use client";

import { useFloatingContext } from "@faire/slate/private/floating/FloatingContext";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useContext,
  createContext,
} from "react";

import { MODAL_ANIMATION_TIME_MS } from "./consts";
import { ModalWidthVariant } from "./types";

export const ModalContext: React.FC<
  React.PropsWithChildren<{
    width?: ModalWidthVariant;
    disableAnimations?: boolean;
  }>
> = ({ children, width, disableAnimations }) => {
  const value = useInitContext(width, disableAnimations);
  return <Context value={value}>{children}</Context>;
};

export const useModalContext = () => {
  const context = useContext(Context);

  if (!context) {
    throw new Error("ModalContext not found");
  }

  return context;
};

const Context = createContext<ReturnType<typeof useInitContext> | undefined>(
  undefined
);

function useInitContext(
  width?: ModalWidthVariant,
  disableAnimations?: boolean
) {
  const { open, setOpen } = useFloatingContext();
  const [contentHeight, setContentHeight] = useState<number>(0);
  const [footerHeight, setFooterHeight] = useState<number>(0);
  const [modalWidth, setModalWidth] = useState(width);

  const closeModal = useCallback(
    (e?: React.MouseEvent<HTMLElement>) => {
      setOpen(false, e?.nativeEvent, "click");
    },
    [setOpen]
  );

  const animating = useAnimating(open);
  const modalHeight = contentHeight + footerHeight;

  useEffect(() => {
    setModalWidth(width);
  }, [width]);

  return useMemo(
    () => ({
      animating: animating && !disableAnimations,
      footerHeight,
      modalWidth,
      modalHeight,
      closeModal,
      setContentHeight,
      setFooterHeight,
    }),
    [
      animating,
      disableAnimations,
      footerHeight,
      modalWidth,
      modalHeight,
      closeModal,
    ]
  );
}

const useAnimating = (open: boolean) => {
  const previousOpenRef = useRef(open);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    const shouldAnimate = open || open !== previousOpenRef.current;
    previousOpenRef.current = open;

    if (shouldAnimate) {
      setAnimating(true);
      const timer = setTimeout(() => {
        setAnimating(false);
      }, MODAL_ANIMATION_TIME_MS);
      return () => clearTimeout(timer);
    }

    return undefined;
  }, [open]);

  return animating;
};
